import CoreAnalytics from './coreAnalytics';

class RecordHealthAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    RECORD_HEALTH_VIEWED: 'record_health_viewed',
    FIELDS_SEARCHED: 'record_health_fields_searched',
    FIELDS_SORTED: 'record_health_fields_sorted',
    FIELD_COUNTED_CHANGED: 'record_health_field_counted_changed',
    UPDATE_CLIENT_DATA_VIEWED: 'update_client_data_viewed',
    UPDATE_CLIENT_DATA_FILTERED: 'update_client_data_filtered',
    UPDATE_CLIENT_DATA_SEARCHED: 'update_client_data_searched',
    UPDATE_CLIENT_DATA_SORTED: 'update_client_data_sorted',
    CLIENT_FIELD_UPDATED: 'update_client_data_field_updated',
  };

  UPDATE_CLIENT_DATA_FILTERS = {
    ALL: 'all',
    HAS_GAPS: 'has_gaps',
  };

  UPDATE_CLIENT_DATA_ENTRY_POINTS = {
    SIDEBAR: 'sidebar',
    BUTTON: 'button',
  };

  /**
   * @param {number|null} [activeClientCount] - Null when the reader has no clinic roster.
   * @returns {void}
   */
  trackRecordHealthViewed(activeClientCount = null) {
    this.track(this.EVENT_NAMES.RECORD_HEALTH_VIEWED, {
      activeClientCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {number} matchedFieldCount - Rows left after the search. The term itself is never sent.
   * @param {number} totalFieldCount - Rows before it.
   * @returns {void}
   */
  trackFieldsSearched(matchedFieldCount, totalFieldCount) {
    this.track(this.EVENT_NAMES.FIELDS_SEARCHED, {
      matchedFieldCount,
      totalFieldCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {string} column - 'field' or 'percent'.
   * @param {string} direction - 'asc' or 'desc'.
   * @returns {void}
   */
  trackFieldsSorted(column, direction) {
    this.track(this.EVENT_NAMES.FIELDS_SORTED, {
      column,
      direction,
      eventVersion: 1,
    });
  }

  /**
   * @param {string} field - A built-in field id, or 'customField'. A custom field's id and label
   *   are the clinic's own data, so neither is sent.
   * @param {boolean} isCounted - Whether it now counts toward the score.
   * @param {number} countedFieldCount - Fields counted after the change.
   * @returns {void}
   */
  trackFieldCountedChanged(field, isCounted, countedFieldCount) {
    this.track(this.EVENT_NAMES.FIELD_COUNTED_CHANGED, {
      field,
      isCounted,
      countedFieldCount,
      eventVersion: 1,
    });
  }

  /**
   * Fires once per visit to Update client data.
   *
   * @param {string} entryPoint - How the reader got there: 'button' from Record health's own
   *   button, 'sidebar' from the menu entry or any other route in.
   * @returns {void}
   */
  trackUpdateClientDataViewed(entryPoint) {
    this.track(this.EVENT_NAMES.UPDATE_CLIENT_DATA_VIEWED, {
      entryPoint,
      eventVersion: 1,
    });
  }

  /**
   * @param {string} filter - One of UPDATE_CLIENT_DATA_FILTERS.
   * @param {number} shownClientCount - Clients the filter left.
   * @returns {void}
   */
  trackUpdateClientDataFiltered(filter, shownClientCount) {
    this.track(this.EVENT_NAMES.UPDATE_CLIENT_DATA_FILTERED, {
      filter,
      shownClientCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {number} matchedClientCount - Clients the search matched. The term is never sent.
   * @param {number} totalClientCount
   * @returns {void}
   */
  trackUpdateClientDataSearched(matchedClientCount, totalClientCount) {
    this.track(this.EVENT_NAMES.UPDATE_CLIENT_DATA_SEARCHED, {
      matchedClientCount,
      totalClientCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {string} column - 'client', 'gaps', a built-in field id, or 'customField'. A custom
   *   field's own id and label are the clinic's data, so neither is sent.
   * @param {string} direction - 'asc' or 'desc'.
   * @returns {void}
   */
  trackUpdateClientDataSorted(column, direction) {
    this.track(this.EVENT_NAMES.UPDATE_CLIENT_DATA_SORTED, {
      column,
      direction,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} update
   * @param {string} update.fieldId - A built-in field id, or 'customField'.
   * @param {boolean} update.isCustomField
   * @param {boolean} update.counted
   * @param {boolean} update.wasEmpty
   * @returns {void}
   */
  trackClientFieldUpdated({
    fieldId, isCustomField, counted, wasEmpty,
  }) {
    this.track(this.EVENT_NAMES.CLIENT_FIELD_UPDATED, {
      fieldId,
      isCustomField,
      counted,
      wasEmpty,
      eventVersion: 1,
    });
  }
}

export default new RecordHealthAnalytics();
